import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdDelete, MdCheckCircle, MdClose } from 'react-icons/md';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDate } from '../utils/format';

export default function Moderation() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const { page, limit, setPage, setLimit } = usePagination(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchReports = async (fetchPage = page, fetchLimit = limit) => {
    window.scrollTo(0, 0);
    try {
      const res = await api.get('/admin/comments/reports', { params: { page: fetchPage, limit: fetchLimit } });
      setReports(res.data.data);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(page, limit); }, [page, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.patch(`/admin/comments/${deleteModal.id}/delete`);
      toast.success('Comment deleted');
      setDeleteModal(null);
      fetchReports();
    } catch (err) {
      toast.error('Failed');
    } finally {
      setDeleting(false);
    }
  };

  const dismissReport = async (reportId) => {
    try {
      await api.patch(`/admin/comments/reports/${reportId}/resolve`);
      toast.success('Report dismissed');
      fetchReports();
    } catch (err) {
      toast.error('Failed');
    }
  };

  if (loading) return <div className="page-loader">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 style={{ margin: '0 0 4px 0' }}>Moderation</h2>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>{pagination.total} pending reviews</p>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
          <h3>All clear!</h3>
          <p>No reported comments to review.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {reports.map(report => (
            <div key={report.id} className="card" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <span className="badge-expired" style={{ display: 'inline-flex' }}>🚨 Reported</span>
                </div>
                {report.Comment?.Article && (
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '60%', textAlign: 'right' }}>on: {report.Comment.Article.title}</span>
                )}
              </div>
              <p style={{ margin: '12px 0', color: 'var(--text)', fontSize: '14px', lineHeight: '1.5', fontStyle: 'italic' }}>"{report.Comment?.body}"</p>
              {report.reason && (
                <p style={{ margin: '8px 0', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  <strong>Reason:</strong> {report.reason}
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{formatDate(report.created_at)}</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-danger" onClick={() => setDeleteModal(report.Comment)}>
                    <MdDelete /> Delete
                  </button>
                  <button className="btn btn-ghost" onClick={() => dismissReport(report.id)}>
                    <MdCheckCircle /> Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {reports.length > 0 && (
        <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Comment</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this comment?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.body}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
